// src/pages/LeaderHierarchy.tsx

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import axios from "axios";
import ReactFlow, {
  Background,
  Controls,
  ReactFlowProvider,
  Node,
  Edge,
  useReactFlow,
} from "reactflow";
import CustomNode from "components/Hierarchy component/CustomNode";
import NodeDetailsModal from "components/Hierarchy component/NodeDetailsModal";
import "reactflow/dist/style.css";
import "./LeaderHierarchy.css";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { Calendar } from "react-feather";
import qs from "qs";
import moment from "moment";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";


interface TreeNode {
  job_title: string;
  user_uid: string;
  name: string;
  department: string;
  self_metrics: any;
  vertical_metrics: any;
  self_metrics_score: number;
  vertical_metrics_score: number;
  overall_performance: number;
  children: TreeNode[];
  horizontal_metrics?: any[];
  location?: string;
}

const extractTreeData = (data: any): TreeNode => {
  return {
    job_title: data.job_title,
    user_uid: data.user_uid,
    name: data.name,
    department: data.department,
    self_metrics: data.self_metrics ?? null,
    vertical_metrics: data.vertical_metrics ?? null,
    overall_performance: data.self_metrics?.data?.[0]?.overall_performance ?? 0,
    self_metrics_score: data.self_metrics?.data?.[0]?.overall_performance ?? 0,
    vertical_metrics_score: data.vertical_metrics?.data?.[0]?.overall_performance ?? 0,
    children: data.children ? data.children.map(extractTreeData) : [],
    horizontal_metrics: data.horizontal_metrics ?? [],
    location: data.location ?? "",
  };
};

// Helper function to find a node by ID
const findNodeById = (nodeId: string, node: TreeNode): TreeNode | null => {
  if (node.user_uid === nodeId) return node;
  for (const child of node.children) {
    const found = findNodeById(nodeId, child);
    if (found) return found;
  }
  return null;
};

// New improved graph generation with sibling hiding logic
const generateGraph = (
  rootNode: TreeNode,
  expandedNodes: Set<string>,
  x = 0,
  y = 0
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const getResponsiveConfig = () => {
    const screenWidth = window.innerWidth;

    if (screenWidth <= 768) {
      return { maxNodesPerRow: 2, horizontalSpacing: 400, verticalSpacing: 215 };
    } else if (screenWidth <= 1024) {
      return { maxNodesPerRow: 3, horizontalSpacing: 400, verticalSpacing: 215 };
    } else {
      return { maxNodesPerRow: 5, horizontalSpacing: 400, verticalSpacing: 215 };
    }
  };

  const getVisibleNodes = (node: TreeNode): TreeNode[] => {
    const visibleNodes: TreeNode[] = [node];
    if (expandedNodes.has(node.user_uid)) {
      const expandedChildren = node.children.filter(child => expandedNodes.has(child.user_uid));
      const childrenToShow = expandedChildren.length > 0 ? expandedChildren : node.children;
      childrenToShow.forEach(child => {
        visibleNodes.push(...getVisibleNodes(child));
      });
    }
    return visibleNodes;
  };

  const visibleNodesList = getVisibleNodes(rootNode);
  const visibleNodesMap = new Map(visibleNodesList.map(node => [node.user_uid, node]));

  const buildHierarchyLevel = (
    levelNodes: TreeNode[],
    currentY: number,
    parentId?: string
  ) => {
    const { maxNodesPerRow, horizontalSpacing, verticalSpacing } = getResponsiveConfig();

    const numRows = Math.ceil(levelNodes.length / maxNodesPerRow);

    levelNodes.forEach((node, index) => {
      const row = Math.floor(index / maxNodesPerRow);
      const positionInRow = index % maxNodesPerRow;
      const nodesInCurrentRow = Math.min(maxNodesPerRow, levelNodes.length - row * maxNodesPerRow);

      const startX = -(nodesInCurrentRow - 1) * horizontalSpacing / 2;
      const nodeX = startX + positionInRow * horizontalSpacing;
      const nodeY = currentY + row * verticalSpacing;

      nodes.push({
        id: node.user_uid,
        position: { x: nodeX, y: nodeY },
        data: { ...node },
        type: "customNode",
      });

      if (parentId) {
        edges.push({
          id: `${parentId}-${node.user_uid}`,
          source: parentId,
          target: node.user_uid,
          animated: true,
        });
      }
    });

    levelNodes.forEach((node) => {
      if (expandedNodes.has(node.user_uid)) {
        const visibleChildren = node.children.filter(child =>
          visibleNodesMap.has(child.user_uid)
        );

        if (visibleChildren.length > 0) {
          const expandedChildren = visibleChildren.filter(child =>
            expandedNodes.has(child.user_uid)
          );

          const childrenToShow = expandedChildren.length > 0 ? expandedChildren : visibleChildren;

          const currentLevelRows = Math.ceil(levelNodes.length / maxNodesPerRow);
          const childrenY = currentY + (currentLevelRows - 1) * verticalSpacing + 280;

          buildHierarchyLevel(childrenToShow, childrenY, node.user_uid);
        }
      }
    });
  };

  buildHierarchyLevel([rootNode], 0);
  return { nodes, edges };
};



const fetchUserImages = async (
  userUids: string[],
  userImages: Record<string, string>,
  setUserImages: React.Dispatch<React.SetStateAction<Record<string, string>>>
) => {
  const uidsToFetch = userUids.filter((uid) => !userImages[uid]);
  if (uidsToFetch.length === 0) return;

  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/user-profile-images`, {
      headers: {
        "user-uid": localStorage.getItem("user_uid") || "",
        accept: "application/json",
      },
      params: {
        icon_img: true,
        target_user_uid_list: uidsToFetch,
      },withCredentials: true,
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
    });

    const blankGif = "R0lGODlhAQABAIABAAAAAP///yH5BAEAAAEALAAAAAABAAEAAAICRAEAOw==";
    const imageMap: Record<string, string> = {};
    response.data?.data?.forEach((entry: any) => {
      if (entry.user_uid && entry.image && entry.image !== blankGif) {
        imageMap[entry.user_uid] = entry.image;
      }
    });

    setUserImages((prev) => ({ ...prev, ...imageMap }));
  } catch (error) {
    console.warn("Failed to fetch user images", error);
  }
};

const LeaderHierarchy = () => {
  useCheckSessionHeartBeat();
  const [hierarchyData, setHierarchyData] = useState<TreeNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNodeData, setSelectedNodeData] = useState<any>(null);
  const hasAutoFittedOnce = useRef(false);
  const shouldAutoFitRef = useRef(false);
  const [topPerformers, setTopPerformers] = useState<any[]>([]);
  const [viewMode, setViewMode] = useState<'reportees' | 'topPerformers'>('reportees');
  const location = useLocation();
  const navigate = useNavigate();
  const leaderId = localStorage.getItem("leader_uid");
  const [userImages, setUserImages] = useState<Record<string, string>>({});
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  const [reporteesGraph, setReporteesGraph] = useState<any[]>([]);
  const [topPerformersGraph, setTopPerformersGraph] = useState<any[]>([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const openModal = (data: any) => {
    setSelectedNodeData(data);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const toggleNode = useCallback((nodeId: string) => {
    if (!hierarchyData) return;

    setExpandedNodes((prev) => {
      const newExpanded = new Set(prev);
      const wasExpanded = newExpanded.has(nodeId);

      if (wasExpanded) {

        const removeDescendants = (node: TreeNode) => {
          newExpanded.delete(node.user_uid);
          node.children.forEach(removeDescendants);
        };

        const targetNode = findNodeById(nodeId, hierarchyData);
        if (targetNode) {
          removeDescendants(targetNode);
        }
      } else {

        newExpanded.add(nodeId);

        const expandedNode = findNodeById(nodeId, hierarchyData);
        const childNodes = expandedNode?.children || [];
        const uidsToFetch = childNodes
          .map((child: any) => child.user_uid)
          .filter((uid: string | undefined) => !!uid);

        fetchUserImages(uidsToFetch, userImages, setUserImages);


        shouldAutoFitRef.current = true;
      }

      return newExpanded;
    });
  }, [hierarchyData, userImages]);


  const AutoFitView: React.FC<{ nodes: Node[] }> = ({ nodes }) => {
    const reactFlowInstance = useReactFlow();
    const { fitView, getNodes, setViewport } = reactFlowInstance || {};

    useEffect(() => {
      if (!getNodes || hasAutoFittedOnce.current) return;
      const allNodes = getNodes();
      if (allNodes.length === 0) return;

      if (fitView) {
        fitView({ padding: 0.3 });
      }

      setTimeout(() => {
        if (setViewport) {
          setViewport({ x: 400, y: 50, zoom: 1 });
        }
        hasAutoFittedOnce.current = true;
      }, 1);
    }, [getNodes, fitView, setViewport, nodes.length]);

    useEffect(() => {
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);


    useEffect(() => {
      if (!fitView || !hasAutoFittedOnce.current) return;

      const timer = setTimeout(() => {
        const allNodes = getNodes?.();
        if (allNodes && allNodes.length > 0) {
          fitView({
            padding: 0.3,
            duration: 600,
            maxZoom: 1.2
          });
        }
      }, 150);

      return () => clearTimeout(timer);
    }, [nodes.length, expandedNodes.size, fitView, getNodes]);

    return null;
  };

  useEffect(() => {
    if (hierarchyData?.user_uid) {
      const rootId = hierarchyData.user_uid;

      if (!userImages[rootId]) {
        const sessionUid = localStorage.getItem("session_uid");
        const userUid = localStorage.getItem("user_uid");

        axios.get(`${process.env.REACT_APP_API_URL}/api/user-profile-images`, {
          headers: {
            "user-uid": userUid || "",
            accept: "application/json",
          },
          params: {
            target_user_uid_list: [rootId],
            icon_img: true,
          },withCredentials: true,
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
        })
          .then(res => {
            const result = res.data?.data || [];
            const imageMap: Record<string, string> = {};
            result.forEach((imgObj: any) => {
              imageMap[imgObj.user_uid] = imgObj.image;
            });
            setUserImages(prev => ({ ...prev, ...imageMap }));
          })
          .catch(err => console.error("Root image fetch failed", err));
      }
    }
  }, [hierarchyData, userImages]);

  const formatDateToLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!leaderId) {
      navigate("/view-teams");
    }
  }, [leaderId, navigate]);

  useEffect(() => {
    const fetchHierarchy = async () => {
      try {
        setLoading(true);
        const sessionUid = localStorage.getItem("session_uid");
        const userUid = localStorage.getItem("user_uid");

        const selected = moment(selectedDate);

        const fromDatetime = selected.clone().startOf("day").format("YYYY-MM-DDTHH:mm:ss");
        const toDatetime = selected
          .clone()
          .set({
            hour: moment().hour(),
            minute: moment().minute(),
            second: moment().second(),
          })
          .format("YYYY-MM-DDTHH:mm:ss");

        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-hierarchy-metrics`, {
          params: {
            from_datetime: fromDatetime,
            to_datetime: toDatetime,
            target_user_uid: leaderId,
          },
          headers: {
            "user-uid": userUid,
            accept: "application/json",
          },withCredentials: true,
        });

        const data = res.data?.data;
        if (!data) throw new Error("Empty response");

        const hierarchyRoot = extractTreeData(data);
        setHierarchyData(hierarchyRoot);

        // --------- Top Performers Graph ---------
        const generateTopPerformersGraph = (leaderData: any): Node[] => {
          const topPerformers = leaderData.top_performers || [];

          if (topPerformers.length === 0) return [];

          const screenWidth = window.innerWidth;
          const spacingY = screenWidth <= 768 ? 200 : screenWidth <= 1024 ? 190 : 180;
          const startX = 0;
          const startY = 100;

          return topPerformers.map((tp: any, index: number) => ({
            id: tp.user_uid,
            type: "customNode",
            position: { x: startX, y: startY + index * spacingY },
            data: {
              ...tp,
              name: tp.user_name,
              job_title: "Top Performer",
              department: "",
              self_metrics: {
                data: tp.data || [],
              },
            },
          }));
        };

        const topGraph = generateTopPerformersGraph(data);
        setTopPerformersGraph(topGraph);

        const topPerformerUids = data.top_performers?.map((tp: any) => tp.user_uid) || [];
        if (topPerformerUids.length > 0) {
          fetchUserImages(topPerformerUids, userImages, setUserImages);
        }

      } catch (err) {
        console.error("Failed to load hierarchy", err);
      } finally {
        setLoading(false);
        hasAutoFittedOnce.current = false;
      }
    };

    fetchHierarchy();
  }, [leaderId, selectedDate]);

  if (loading) return <p>Loading hierarchy...</p>;
  if (!hierarchyData) return <p>No hierarchy data found.</p>;

  // Generate nodes and edges based on view mode
  const { nodes, edges } = viewMode === "reportees"
    ? generateGraph(hierarchyData, expandedNodes)
    : { nodes: topPerformersGraph, edges: [] };

  return (
    <div className="leader-hierarchy-container">
      <h3 style={{ margin: "0px", marginBottom: "5px" }}>Leader Hierarchy</h3>

      {/* Toggle Buttons */}
      <div className="LeaderHeirarchy-toggle-buttons">
        <button
          className={`LeaderHeirarchy-toggle-btn ${viewMode === "reportees" ? "active" : ""}`}
          onClick={() => setViewMode("reportees")}
        >
          Reportees
        </button>
        <button
          className={`LeaderHeirarchy-toggle-btn ${viewMode === "topPerformers" ? "active" : ""}`}
          onClick={() => setViewMode("topPerformers")}
        >
          Top Performers
        </button>
        <div className="LeaderHeirarchy-calendar-wrapper">
          <Calendar className="LeaderHierarchy-calendar-icon" size={16} />
          <Flatpickr
            value={selectedDate}
            options={{
              dateFormat: "Y-m-d",
              maxDate: "today",
            }}
            onChange={(date: Date[]) => {
              if (date[0]) setSelectedDate(date[0]);
            }}
            className="LeaderHeirarchy-flatpickr-input"
            placeholder="Select date"
          />
        </div>
      </div>

      {viewMode === "topPerformers" && (!topPerformersGraph || topPerformersGraph.length === 0) && (
        <p style={{ textAlign: "center", marginTop: "20px", fontSize: "14px", color: "#888" }}>
          No top performers available.
        </p>
      )}

      <ReactFlowProvider>
        <div className="reactflow-wrapper">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            style={{ backgroundColor: '#f4f4f4' }}
            nodeTypes={{
              customNode: (props) => (
                <CustomNode
                  {...props}
                  toggleNode={toggleNode}
                  expanded={expandedNodes.has(props.id)}
                  openModal={openModal}
                  userImages={userImages}
                />
              ),
            }}
            panOnScroll
            zoomOnScroll={false}
            panOnDrag
            zoomOnDoubleClick={false}
            fitView
            fitViewOptions={{ padding: 0.3 }}
          >
            <AutoFitView nodes={nodes} />
            <Controls showInteractive={false} />
            {/* <Background /> => removed! */}
          </ReactFlow>
        </div>
      </ReactFlowProvider>


      <NodeDetailsModal
        isOpen={modalOpen}
        onClose={closeModal}
        data={selectedNodeData}
        userImages={userImages}
      />
    </div>
  );
};

export default LeaderHierarchy;